import React, { useState, useEffect } from 'react';
import {
	CheckCircleIcon,
	XCircleIcon,
	ChatBubbleLeftRightIcon,
	CalendarIcon,
	ArrowPathIcon,
	Cog6ToothIcon,
	BriefcaseIcon,
	BellIcon
} from '@heroicons/react/24/outline';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import toast from 'react-hot-toast';
import {
	getMessengerAccounts,
	createMessengerAccount,
	deleteMessengerAccount,
	startTelegramAuth,
	verifyTelegramCode,
	verifyTelegram2FA,
	getWhatsAppSettings,
	saveWhatsAppSettings,
	deleteWhatsAppSettings,
	getIntegrations,
	getCalendarSettings,
	saveCalendarSettings,
	clearCalendarSettings,
	getGoogleCalendarAuthUrl,
	connectYandexCalendar,
	disconnectIntegration,
	type MessengerAccount,
	type Integration,
	type CalendarSettings,
	type WhatsAppSettings
} from '../services/messengerService';
import { useBrandingColors } from '../hooks/useBrandingColors';
import HHAuthContent from '../relevanter/components/HHAuthContent';
import AvitoAuthContent from '../relevanter/components/AvitoAuthContent';
import ATSIntegrationContent from './ATSIntegrationContent';
import { TelegramNotificationCard } from './settings/TelegramNotificationCard';
import { NotificationPreferencesCard } from './settings/NotificationPreferencesCard';
import MaxAuthDialog from './messenger/MaxAuthDialog';

type MessengerId = 'telegram' | 'whatsapp' | 'max';
type MessengerAccountsByType = Record<MessengerId, MessengerAccount[]>;

const emptyMessengerAccounts = (): MessengerAccountsByType => ({
	telegram: [],
	whatsapp: [],
	max: []
});

const normalizeTelegramPhoneNumber = (phoneNumber: string): string =>
	phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

/**
 * REL-209: единый формат статуса подключения мессенджер-аккаунта.
 * EXPIRED/LOGGED_OUT показываем красным как «Требуется переподключение».
 */
const formatAuthStatus = (status?: string): { label: string; className: string } => {
	switch (status) {
		case 'ACTIVE':
			return { label: 'Активен', className: 'text-green-600' };
		case 'AUTHENTICATED':
			return { label: 'Подключен', className: 'text-green-600' };
		case 'PENDING':
			return { label: 'Ожидание', className: 'text-yellow-600' };
		case 'ERROR':
			return { label: 'Ошибка', className: 'text-red-600' };
		case 'EXPIRED':
		case 'LOGGED_OUT':
			return { label: 'Требуется переподключение', className: 'text-red-600' };
		default:
			return { label: status || '', className: 'text-gray-600' };
	}
};

export const IntegrationsContent: React.FC = () => {
	const colors = useBrandingColors();
	const [activeTab, setActiveTab] = useState('messengers');
	const [activeMessenger, setActiveMessenger] = useState<MessengerId>('telegram');
	const [loading, setLoading] = useState(false);
	const [loadingAccounts, setLoadingAccounts] = useState(false);

	// Messengers state
	const [messengerAccounts, setMessengerAccounts] = useState<MessengerAccountsByType>(emptyMessengerAccounts());

	// Form states for adding new accounts
	const [telegramForm, setTelegramForm] = useState({
		phoneNumber: '',
		apiId: '',
		apiHash: ''
	});
	const [telegramPhoneError, setTelegramPhoneError] = useState('');

	// WhatsApp Dialog360 global settings
	const [whatsappSettings, setWhatsappSettings] = useState<WhatsAppSettings>({
		configured: false,
		apiKeyMasked: '',
		apiKey: '',
		phoneNumber: ''
	});
	const [loadingWhatsApp, setLoadingWhatsApp] = useState(false);
	const [whatsappPhoneError, setWhatsappPhoneError] = useState('');

	// Telegram authentication state
	const [authModal, setAuthModal] = useState({
		isOpen: false,
		phoneNumber: '',
		sessionId: '',
		step: 'code' as 'code' | '2fa',
		code: '',
		password: ''
	});

	const mergeMessengerAccount = (account: MessengerAccount) => {
		const type = account.messengerType?.toLowerCase() as MessengerId | undefined;
		if (!type || !['telegram', 'whatsapp', 'max'].includes(type)) {
			return;
		}

		setMessengerAccounts(prev => {
			const currentAccounts = prev[type];
			const accountExists = currentAccounts.some(item => String(item.id) === String(account.id));
			const nextAccounts = accountExists
				? currentAccounts.map(item => String(item.id) === String(account.id) ? account : item)
				: [account, ...currentAccounts];

			return {
				...prev,
				[type]: nextAccounts
			};
		});
	};

	const removeMessengerAccount = (accountId: string | number) => {
		setMessengerAccounts(prev => ({
			telegram: prev.telegram.filter(account => String(account.id) !== String(accountId)),
			whatsapp: prev.whatsapp.filter(account => String(account.id) !== String(accountId)),
			max: prev.max.filter(account => String(account.id) !== String(accountId))
		}));
	};

	// Max authentication dialog state
	const [maxAuthDialogOpen, setMaxAuthDialogOpen] = useState(false);

	// Google Calendar integration state
	const [calendarIntegrations, setCalendarIntegrations] = useState<Integration[]>([]);
	const [loadingCalendar, setLoadingCalendar] = useState(false);
	const [connectingCalendar, setConnectingCalendar] = useState(false);
	const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>({
		clientId: '',
		clientSecret: '',
		redirectUri: ''
	});
	const [showSettings, setShowSettings] = useState(false);
	const [savingSettings, setSavingSettings] = useState(false);
	const [clearingSettings, setClearingSettings] = useState(false);

	const filterPhoneInput = (value: string): string => {
		return value.replace(/[^+\d]/g, '');
	};

	const validatePhoneNumber = (value: string): string => {
		const trimmed = value.trim();
		if (!trimmed) {
			return 'Введите номер телефона';
		}
		if (!trimmed.startsWith('+')) {
			return 'Номер должен начинаться с + и кода страны';
		}
		try {
			const phone = parsePhoneNumberFromString(trimmed);
			if (!phone || !phone.isValid()) {
				return 'Введите корректный номер телефона';
			}
			return '';
		} catch {
			return 'Введите корректный номер телефона';
		}
	};

	useEffect(() => {
		fetchMessengerAccounts();
		fetchCalendarIntegrations();
		fetchWhatsAppSettingsData();
	}, []);

	const fetchWhatsAppSettingsData = async () => {
		setLoadingWhatsApp(true);
		try {
			const settings = await getWhatsAppSettings();
			setWhatsappSettings(settings);
		} catch (error) {
			console.error('Error fetching WhatsApp settings:', error);
			setWhatsappSettings({
				configured: false,
				apiKeyMasked: '',
				phoneNumber: '',
				apiKey: ''
			});
		} finally {
			setLoadingWhatsApp(false);
		}
	};

	const fetchMessengerAccounts = async () => {
		setLoadingAccounts(true);
		try {
			const response = await getMessengerAccounts();

			const accounts = emptyMessengerAccounts();

			const accountsArray = Array.isArray(response) ? response : [];

			accountsArray.forEach(account => {
				if (account.isActive === false) {
					return;
				}
				const type = account.messengerType?.toLowerCase();
				if (type && accounts[type as keyof typeof accounts]) {
					accounts[type as keyof typeof accounts].push(account);
				}
			});

			setMessengerAccounts(accounts);
		} catch (error) {
			console.error('Error fetching messenger accounts:', error);
		} finally {
			setLoadingAccounts(false);
		}
	};

	const handleAddTelegramAccount = async () => {
		const validationError = validatePhoneNumber(telegramForm.phoneNumber);
		if (validationError) {
			setTelegramPhoneError(validationError);
			return;
		}

		setLoading(true);
		try {
			const phoneNumber = normalizeTelegramPhoneNumber(telegramForm.phoneNumber);
			
			const createdAccount = await createMessengerAccount({
				messengerType: 'TELEGRAM',
				phoneNumber: phoneNumber,
				accountIdentifier: phoneNumber,
				accountName: `Telegram ${phoneNumber}`,
				credentials: JSON.stringify({
					apiId: telegramForm.apiId,
					apiHash: telegramForm.apiHash
				}),
				authType: 'USER',
				authStatus: 'PENDING'
			});

			const accountForAuth: MessengerAccount = {
				...createdAccount,
				messengerType: 'TELEGRAM',
				phoneNumber: createdAccount.phoneNumber || phoneNumber,
				accountIdentifier: createdAccount.accountIdentifier || phoneNumber,
				accountName: createdAccount.accountName || `Telegram ${phoneNumber}`,
				authStatus: createdAccount.authStatus || 'PENDING'
			};

			toast.success('Telegram аккаунт добавлен, запрашиваем код подтверждения');
			mergeMessengerAccount(accountForAuth);
			setTelegramForm({ phoneNumber: '', apiId: '', apiHash: '' });
			setTelegramPhoneError('');
			await handleStartTelegramAuth(accountForAuth);
		} catch (error) {
			toast.error('Ошибка добавления Telegram аккаунта');
			console.error('Error adding Telegram account:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSaveWhatsAppSettings = async () => {
		if (whatsappSettings.phoneNumber?.trim()) {
			const validationError = validatePhoneNumber(whatsappSettings.phoneNumber);
			if (validationError) {
				setWhatsappPhoneError(validationError);
				return;
			}
			setWhatsappPhoneError('');
		} else {
			setWhatsappPhoneError('');
		}

		if (!whatsappSettings.apiKey?.trim() && !whatsappSettings.phoneNumber?.trim()) {
			toast.error('Введите API ключ или номер телефона');
			return;
		}

		setLoading(true);
		try {
			const data: { apiKey?: string; phoneNumber?: string } = {};
			if (whatsappSettings.apiKey?.trim()) {
				data.apiKey = whatsappSettings.apiKey.trim();
			}
			if (whatsappSettings.phoneNumber?.trim()) {
				data.phoneNumber = whatsappSettings.phoneNumber.trim();
			}

			await saveWhatsAppSettings(data);
			toast.success('WhatsApp настройки сохранены');
			setWhatsappSettings(prev => ({ ...prev, apiKey: '' }));
			fetchWhatsAppSettingsData();
		} catch (error) {
			toast.error('Ошибка сохранения настроек WhatsApp');
			console.error('Error saving WhatsApp settings:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteWhatsAppSettings = async () => {
		if (!window.confirm('Вы уверены, что хотите отключить WhatsApp?')) {
			return;
		}

		setLoading(true);
		try {
			await deleteWhatsAppSettings();
			toast.success('WhatsApp отключен');
			fetchWhatsAppSettingsData();
		} catch (error) {
			toast.error('Ошибка отключения WhatsApp');
			console.error('Error deleting WhatsApp settings:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteAccount = async (accountId: string | number) => {
		if (!window.confirm('Вы уверены, что хотите удалить этот аккаунт?')) {
			return;
		}

		try {
			await deleteMessengerAccount(accountId);
			toast.success('Аккаунт удален');
			removeMessengerAccount(accountId);
			fetchMessengerAccounts();
		} catch (error) {
			toast.error('Ошибка удаления аккаунта');
			console.error('Error deleting account:', error);
		}
	};

	const handleStartTelegramAuth = async (account: MessengerAccount) => {
		setLoading(true);
		try {
			const phoneNumber = account.phoneNumber || '';
			const accountName = account.accountName || '';
			const normalizedPhoneNumber = normalizeTelegramPhoneNumber(phoneNumber);
			const response = await startTelegramAuth(
				normalizedPhoneNumber,
				accountName,
				{ accountId: account.id }
			);

			if (response.status === 'WAITING_CODE' || response.status === 'WAITING_FOR_CODE') {
				setAuthModal({
					isOpen: true,
					phoneNumber: normalizedPhoneNumber,
					sessionId: response.sessionId || '',
					step: 'code',
					code: '',
					password: ''
				});
				toast.success('Код отправлен на ваш Telegram');
			} else if (response.status === 'AUTHENTICATED') {
				toast.success('Аккаунт успешно аутентифицирован');
				fetchMessengerAccounts();
			} else {
				toast.error(response.message || 'Ошибка аутентификации');
			}
		} catch (error) {
			toast.error('Ошибка начала аутентификации');
			console.error('Error starting auth:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyTelegramCode = async () => {
		setLoading(true);
		try {
			const response = await verifyTelegramCode(authModal.sessionId, authModal.code);

			if (response.status === 'WAITING_2FA' || response.status === 'WAITING_FOR_2FA') {
				setAuthModal({ ...authModal, step: '2fa', code: '', password: '' });
				toast('Введите пароль двухфакторной аутентификации');
			} else if (response.status === 'AUTHENTICATED' || response.status === 'SUCCESS') {
				toast.success('Аккаунт успешно аутентифицирован');
				setAuthModal({ isOpen: false, phoneNumber: '', sessionId: '', step: 'code', code: '', password: '' });
				fetchMessengerAccounts();
			} else {
				toast.error(response.message || 'Неверный код');
			}
		} catch (error) {
			toast.error('Ошибка проверки кода');
			console.error('Error verifying code:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleVerify2FA = async () => {
		setLoading(true);
		try {
			const response = await verifyTelegram2FA(authModal.sessionId, authModal.password);

			if (response.status === 'AUTHENTICATED') {
				toast.success('Аккаунт успешно аутентифицирован');
				setAuthModal({ isOpen: false, phoneNumber: '', sessionId: '', step: 'code', code: '', password: '' });
				fetchMessengerAccounts();
			} else {
				toast.error(response.message || 'Неверный пароль');
			}
		} catch (error) {
			toast.error('Ошибка проверки пароля');
			console.error('Error verifying 2FA:', error);
		} finally {
			setLoading(false);
		}
	};

	const fetchCalendarIntegrations = async () => {
		setLoadingCalendar(true);
		try {
			const integrations = await getIntegrations();
			setCalendarIntegrations(integrations);

			// Fetch settings
			try {
				const settings = await getCalendarSettings();
				if (settings && settings.clientId) {
					setCalendarSettings({
						clientId: settings.clientId || '',
						clientSecret: settings.clientSecret || '',
						redirectUri: settings.redirectUri || ''
					});
				} else {
					setCalendarSettings({ clientId: '', clientSecret: '', redirectUri: '' });
				}
			} catch {
				// Настройки календаря могут ещё не существовать для нового тенанта.
			}
		} catch (error) {
			console.error('Error fetching calendar integrations:', error);
			setCalendarIntegrations([]);
		} finally {
			setLoadingCalendar(false);
		}
	};

	const handleSaveCalendarSettings = async () => {
		setSavingSettings(true);
		try {
			await saveCalendarSettings(calendarSettings);
			toast.success('Настройки сохранены');
			setShowSettings(false);
		} catch (error) {
			console.error('Error saving calendar settings:', error);
			toast.error('Не удалось сохранить настройки');
		} finally {
			setSavingSettings(false);
		}
	};

	const handleClearCalendarSettings = async () => {
		const confirmed = window.confirm(
			'Очистить сохранённые настройки OAuth 2.0? Поля вернутся к пустым значениям по умолчанию.'
		);
		if (!confirmed) return;

		setClearingSettings(true);
		try {
			await clearCalendarSettings();
			setCalendarSettings({ clientId: '', clientSecret: '', redirectUri: '' });
			toast.success('Настройки очищены');
		} catch (error) {
			console.error('Error clearing calendar settings:', error);
			toast.error('Не удалось очистить настройки');
		} finally {
			setClearingSettings(false);
		}
	};

	const handleConnectGoogleCalendar = async () => {
		// Single-active модель (TASKNEIROKLYUCH-429): если уже подключён другой
		// календарь, предупреждаем что он будет удалён вместе с историей токенов.
		if (isAnotherCalendarConnected('GOOGLE_CALENDAR')) {
			if (!window.confirm(SWITCH_CALENDAR_CONFIRM)) return;
		}
		setConnectingCalendar(true);
		try {
			// Get OAuth URL from backend
			const { authUrl } = await getGoogleCalendarAuthUrl();

			// Open OAuth window
			const width = 600;
			const height = 700;
			const left = window.screenX + (window.outerWidth - width) / 2;
			const top = window.screenY + (window.outerHeight - height) / 2;

			const authWindow = window.open(
				authUrl,
				'Google Calendar Authorization',
				`width=${width},height=${height},left=${left},top=${top}`
			);

			// Poll for window close
			const pollTimer = setInterval(() => {
				if (authWindow?.closed) {
					clearInterval(pollTimer);
					setConnectingCalendar(false);
					fetchCalendarIntegrations(); // Reload to get updated status
					toast.success('Проверьте статус подключения');
				}
			}, 500);

		} catch (error) {
			console.error('Failed to connect Google Calendar:', error);
			toast.error('Не удалось подключить Google Calendar');
			setConnectingCalendar(false);
		}
	};

	const handleDisconnectGoogleCalendar = async () => {
		try {
			await disconnectIntegration('GOOGLE_CALENDAR');
			toast.success('Google Calendar отключен');
			fetchCalendarIntegrations();
		} catch (error) {
			console.error('Failed to disconnect Google Calendar:', error);
			toast.error('Не удалось отключить Google Calendar');
		}
	};

	const isGoogleCalendarConnected = () => {
		return calendarIntegrations.some(i => i.type === 'GOOGLE_CALENDAR' && i.status === 'CONNECTED');
	};

	// TASKNEIROKLYUCH-429: Yandex Calendar (через CalDAV + Basic Auth).
	// У Яндекса нет отдельного API/OAuth для Календаря — авторизация идёт обычным
	// логином и паролем приложения, который пользователь создаёт в passport.yandex.ru.
	const [yandexLogin, setYandexLogin] = useState('');
	const [yandexAppPassword, setYandexAppPassword] = useState('');
	const [connectingYandexCalendar, setConnectingYandexCalendar] = useState(false);

	const handleConnectYandexCalendar = async () => {
		const login = yandexLogin.trim();
		const password = yandexAppPassword.trim();
		if (!login || !password) {
			toast.error('Укажите email Яндекса и пароль приложения');
			return;
		}
		// Single-active модель (TASKNEIROKLYUCH-429): предупреждаем что
		// предыдущий календарь будет удалён.
		if (isAnotherCalendarConnected('YANDEX_CALENDAR')) {
			if (!window.confirm(SWITCH_CALENDAR_CONFIRM)) return;
		}
		setConnectingYandexCalendar(true);
		try {
			await connectYandexCalendar(login, password);
			toast.success('Яндекс.Календарь подключён');
			setYandexAppPassword(''); // не держим секрет в state дольше нужного
			fetchCalendarIntegrations();
		} catch (error: any) {
			console.error('Failed to connect Yandex Calendar:', error);
			const status = error?.response?.status;
			const message = error?.response?.data?.message;
			if (status === 401) {
				toast.error(message || 'Логин или пароль приложения отвергнуты Яндексом');
			} else {
				toast.error(message || 'Не удалось подключить Яндекс.Календарь');
			}
		} finally {
			setConnectingYandexCalendar(false);
		}
	};

	const handleDisconnectYandexCalendar = async () => {
		try {
			await disconnectIntegration('YANDEX_CALENDAR');
			toast.success('Яндекс.Календарь отключён');
			fetchCalendarIntegrations();
		} catch (error) {
			console.error('Failed to disconnect Yandex Calendar:', error);
			toast.error('Не удалось отключить Яндекс.Календарь');
		}
	};

	const isYandexCalendarConnected = () => {
		return calendarIntegrations.some(i => i.type === 'YANDEX_CALENDAR' && i.status === 'CONNECTED');
	};

	// Проверка подключённого календаря-другого типа (для single-active модели):
	// если пользователь хочет подключить Yandex, а Google уже стоит — нужно
	// предупредить о замене. Работает для любого нового типа (Outlook и пр.).
	const isAnotherCalendarConnected = (newType: string) => {
		return calendarIntegrations.some(
			i => i.type !== newType && i.status === 'CONNECTED'
		);
	};

	const SWITCH_CALENDAR_CONFIRM =
		'У вас уже подключён другой календарь. При подключении нового он будет '
		+ 'отключён и удалён из настроек. Продолжить?';

	const tabs = [
		{ id: 'headhunter', name: 'Head Hunter', icon: BriefcaseIcon },
		{ id: 'avito', name: 'Авито', icon: BriefcaseIcon },
		{ id: 'ats', name: 'ATS', icon: BriefcaseIcon },
		{ id: 'messengers', name: 'Мессенджеры', icon: ChatBubbleLeftRightIcon },
		{ id: 'notifications', name: 'Нотификации', icon: BellIcon },
		{ id: 'calendar', name: 'Календари', icon: Cog6ToothIcon }
	];

	const messengerOptions: Array<{ id: MessengerId; name: string }> = [
		{ id: 'telegram', name: 'Telegram' },
		{ id: 'whatsapp', name: 'WhatsApp' },
		{ id: 'max', name: 'Max' }
	];

	return (
		<div className="bg-white rounded-[24px] mt-4 mr-4 flex flex-col h-[calc(100vh-120px)]">
			{/* Tabs */}
			<div className="border-b border-gray-200 shrink-0">
				<nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
					{tabs.map((tab) => {
						const Icon = tab.icon;
						return (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
										? 'text-gray-900'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
									}
                `}
								style={activeTab === tab.id ? { borderColor: colors.primary, color: colors.primary } : {}}
							>
								<Icon className="h-5 w-5" />
								{tab.name}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-y-auto p-6">
				{/* Head Hunter Tab */}
				{activeTab === 'headhunter' && (
					<div className="space-y-6">
						<h2 className="text-lg font-semibold text-gray-900">Авторизация HH.ru</h2>
						<HHAuthContent />
					</div>
				)}

				{activeTab === 'avito' && (
					<div className="space-y-6">
						<h2 className="text-lg font-semibold text-gray-900">Авторизация Авито</h2>
						<AvitoAuthContent />
					</div>
				)}

				{activeTab === 'ats' && (
					<div className="space-y-6">
						<h2 className="text-lg font-semibold text-gray-900">Настройка ATS</h2>
						<ATSIntegrationContent />
					</div>
				)}

				{/* Messengers Tab — TASKNEIROKLYUCH-647: объединённый раздел Telegram/WhatsApp/Max */}
				{activeTab === 'messengers' && (
					<div className="flex gap-6">
						{/* Left messenger selector */}
						<div className="w-48 shrink-0 border-r border-gray-200 pr-4 space-y-1">
							{messengerOptions.map((opt) => {
								const isActive = activeMessenger === opt.id;
								return (
									<button
										key={opt.id}
										onClick={() => setActiveMessenger(opt.id)}
										className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors ${isActive ? '' : 'text-gray-600 hover:bg-gray-50'
											}`}
										style={isActive ? { backgroundColor: `${colors.primary}15`, color: colors.primary } : {}}
									>
										<ChatBubbleLeftRightIcon className="h-5 w-5" />
										{opt.name}
									</button>
								);
							})}
						</div>

						{/* Right content */}
						<div className="flex-1 min-w-0">
							{activeMessenger === 'telegram' && (
								<div className="space-y-6">
									<div className="flex items-center justify-between">
										<h2 className="text-lg font-semibold text-gray-900">Подключенные Telegram аккаунты</h2>
										<button
											onClick={fetchMessengerAccounts}
											disabled={loadingAccounts}
											className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
										>
											{loadingAccounts ? '⏳ Загрузка...' : '🔄 Обновить'}
										</button>
									</div>

									{/* Loading indicator */}
									{loadingAccounts ? (
										<div className="flex items-center justify-center py-8">
											<div className="text-gray-500">
												<div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderColor: colors.primary }}></div>
												<p>Загрузка аккаунтов...</p>
											</div>
										</div>
									) : (
										<>
											{/* List of accounts */}
											{messengerAccounts.telegram.length > 0 ? (
												<div className="space-y-3">
													{messengerAccounts.telegram.map((account) => (
														<div key={account.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
															<div className="flex-1">
																<div className="flex items-center gap-3">
																	<p className="font-medium text-gray-900">
																		{account.displayName || account.accountName || 'Telegram Account'}
																	</p>
																	{account.accountIdentifier && account.accountIdentifier.startsWith('@') && (
																		<span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}>
																			{account.accountIdentifier}
																		</span>
																	)}
																</div>
																<div className="text-sm text-gray-500 mt-1">
																	{account.phoneNumber && (
																		<span>📱 {account.phoneNumber}</span>
																	)}
																	{account.authStatus && (
																		<span className="ml-3">
																			Статус: <span className={`font-medium ${formatAuthStatus(account.authStatus).className}`}>{formatAuthStatus(account.authStatus).label}</span>
																		</span>
																	)}
																</div>
																{account.credentials && (() => {
																	try {
																		const creds = JSON.parse(account.credentials);
																		return (
																			<p className="text-xs text-gray-400 mt-1">
																				API ID: {creds.apiId || creds.api_id}
																				{(creds.apiHashConfigured || creds.api_hash_configured) ? ' • API Hash сохранён' : ''}
																			</p>
																		);
																	} catch (e) {
																		return null;
																	}
																})()}
															</div>
															<div className="flex items-center">
																{account.authStatus === 'PENDING' && (
																	<button
																		onClick={() => handleStartTelegramAuth(account)}
																		className="ml-4 px-3 py-1 border rounded-md text-sm"
																		style={{ borderColor: colors.primary, color: colors.primary }}
																		disabled={loading}
																	>
																		Аутентифицировать
																	</button>
																)}
																<button
																	onClick={() => handleDeleteAccount(account.id)}
																	className="ml-4 text-red-600 hover:text-red-700 p-2"
																	title="Удалить аккаунт"
																>
																	<XCircleIcon className="h-5 w-5" />
																</button>
															</div>
														</div>
													))}
												</div>
											) : (
												<div className="text-gray-500 text-center py-4">
													Нет подключенных Telegram аккаунтов
												</div>
											)}
										</>
									)}

									{/* Add new account form */}
									<div className="border-t pt-6">
										<h3 className="text-md font-medium text-gray-900 mb-4">Добавить новый аккаунт</h3>
										<div className="space-y-4">
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Номер телефона
												</label>
												<input
													type="tel"
													placeholder="+79161234567"
													value={telegramForm.phoneNumber}
													onChange={(e) => {
														const filtered = filterPhoneInput(e.target.value);
														setTelegramForm({ ...telegramForm, phoneNumber: filtered });
														if (telegramPhoneError) {
															setTelegramPhoneError('');
														}
													}}
													className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${telegramPhoneError ? 'border-red-500' : 'border-gray-300'}`}
													style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
												/>
												{telegramPhoneError && (
													<p className="mt-1 text-xs text-red-600">
														{telegramPhoneError}
													</p>
												)}
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													API ID
												</label>
												<input
													type="text"
													placeholder="12345678"
													value={telegramForm.apiId}
													onChange={(e) => setTelegramForm({ ...telegramForm, apiId: e.target.value })}
													className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
													style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
												/>
												<p className="text-xs text-gray-500 mt-1">
													Получить на <a href="https://my.telegram.org/apps" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: colors.primary }}>my.telegram.org/apps</a>
												</p>
											</div>
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													API Hash
												</label>
												<input
													type="text"
													placeholder="abcdef1234567890"
													value={telegramForm.apiHash}
													onChange={(e) => setTelegramForm({ ...telegramForm, apiHash: e.target.value })}
													className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
													style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
												/>
											</div>
											<button
												onClick={handleAddTelegramAccount}
												disabled={loading || !telegramForm.phoneNumber || !telegramForm.apiId || !telegramForm.apiHash}
												className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
												style={{ backgroundColor: colors.primary }}
												onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primaryHover)}
												onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.primary)}
											>
												{loading ? 'Добавление...' : 'Добавить аккаунт'}
											</button>
										</div>
									</div>
								</div>
							)}

							{activeMessenger === 'whatsapp' && (
								<div className="space-y-6">
									<h2 className="text-lg font-semibold text-gray-900">WhatsApp (Dialog360)</h2>

									{loadingWhatsApp ? (
										<div className="flex items-center justify-center py-8">
											<div className="text-gray-500">
												<div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-2" style={{ borderColor: colors.primary }}></div>
												<p>Загрузка...</p>
											</div>
										</div>
									) : (
										<>
											{/* Status Card */}
											<div className="bg-white rounded-lg border border-gray-200 p-6">
												<div className="flex items-start justify-between">
													<div className="flex items-start gap-4">
														<div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
															<ChatBubbleLeftRightIcon className="w-7 h-7 text-green-600" />
														</div>
														<div>
															<h3 className="text-lg font-semibold text-gray-900">Dialog360 WhatsApp API</h3>
															<p className="mt-1 text-sm text-gray-600">
																Глобальная интеграция WhatsApp через Dialog360
															</p>
															<div className="mt-3 flex flex-col gap-1">
																<div className="flex items-center gap-2">
																	{whatsappSettings.configured ? (
																		<>
																			<CheckCircleIcon className="w-5 h-5 text-green-500" />
																			<span className="text-sm text-green-600 font-medium">Настроено</span>
																			{whatsappSettings.apiKeyMasked && (
																				<span className="text-sm text-gray-500 ml-2">
																					API Key: {whatsappSettings.apiKeyMasked}
																				</span>
																			)}
																		</>
																	) : (
																		<>
																			<XCircleIcon className="w-5 h-5 text-gray-400" />
																			<span className="text-sm text-gray-500">Не настроено</span>
																		</>
																	)}
																</div>
																{whatsappSettings.phoneNumber && (
																	<div className="text-sm text-gray-600">
																		Номер: <span className="font-medium">{whatsappSettings.phoneNumber}</span>
																	</div>
																)}
															</div>
														</div>
													</div>

													{whatsappSettings.configured && (
														<button
															onClick={handleDeleteWhatsAppSettings}
															disabled={loading}
															className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
														>
															Отключить
														</button>
													)}
												</div>
											</div>

											{/* Settings Form */}
											<div className="bg-white rounded-lg border border-gray-200 p-6">
												<h3 className="text-md font-semibold text-gray-900 mb-4">
													{whatsappSettings.configured ? 'Обновить настройки' : 'Настроить WhatsApp'}
												</h3>
												<div className="space-y-4">
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">
															D360-API-KEY
														</label>
														<input
															type="password"
															placeholder={whatsappSettings.configured ? "Оставьте пустым, чтобы не менять" : "Введите API ключ от Dialog360"}
															value={whatsappSettings.apiKey || ''}
															onChange={(e) => setWhatsappSettings({ ...whatsappSettings, apiKey: e.target.value })}
															className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
															style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
														/>
														<p className="text-xs text-gray-500 mt-1">
															Получите API ключ в{' '}
															<a
																href="https://hub.360dialog.com/"
																target="_blank"
																rel="noopener noreferrer"
																className="hover:underline"
																style={{ color: colors.primary }}
															>
																360dialog Hub
															</a>
														</p>
													</div>
													<div>
														<label className="block text-sm font-medium text-gray-700 mb-1">
															Номер телефона WhatsApp
														</label>
														<input
															type="text"
															placeholder="+7 (901) 581-73-70"
															value={whatsappSettings.phoneNumber || ''}
															onChange={(e) => {
																const filtered = filterPhoneInput(e.target.value);
																setWhatsappSettings({ ...whatsappSettings, phoneNumber: filtered });
																if (whatsappPhoneError) {
																	setWhatsappPhoneError('');
																}
															}}
															className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${whatsappPhoneError ? 'border-red-500' : 'border-gray-300'}`}
															style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
														/>
														{whatsappPhoneError && (
															<p className="mt-1 text-xs text-red-600">
																{whatsappPhoneError}
															</p>
														)}
														<p className="text-xs text-gray-500 mt-1">
															Номер для отображения в интерфейсе
														</p>
													</div>
													<button
														onClick={handleSaveWhatsAppSettings}
														disabled={loading || (!whatsappSettings.apiKey && !whatsappSettings.phoneNumber)}
														className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
														style={{ backgroundColor: colors.primary }}
														onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
														onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.primary)}
													>
														{loading ? 'Сохранение...' : 'Сохранить'}
													</button>
												</div>
											</div>

											{/* Info */}
											<div className="rounded-lg p-4 border" style={{ backgroundColor: `${colors.primary}10`, borderColor: `${colors.primary}30` }}>
												<h4 className="text-sm font-medium mb-2" style={{ color: colors.primary }}>Как это работает:</h4>
												<ul className="list-disc list-inside space-y-1 text-sm" style={{ color: colors.primary }}>
													<li>API ключ используется для всей системы</li>
													<li>Первое сообщение клиенту отправляется через шаблон</li>
													<li>После ответа клиента можно отправлять обычные сообщения</li>
													<li>Входящие сообщения принимаются через webhook</li>
												</ul>
											</div>
										</>
									)}
								</div>
							)}

							{activeMessenger === 'max' && (
								<div className="space-y-6">
									<h2 className="text-lg font-semibold text-gray-900">Подключенные Max аккаунты</h2>

									{/* List of accounts */}
									{messengerAccounts.max.length > 0 ? (
										<div className="space-y-3">
											{messengerAccounts.max.map((account) => (
												<div key={account.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
													<div>
														<p className="font-medium text-gray-900">{account.accountName}</p>
														<p className="text-sm text-gray-500">
															Статус: <span className={`font-medium ${formatAuthStatus(account.authStatus).className}`}>{formatAuthStatus(account.authStatus).label}</span>
														</p>
													</div>
													<button
														onClick={() => handleDeleteAccount(account.id)}
														className="text-red-600 hover:text-red-700"
													>
														<XCircleIcon className="h-5 w-5" />
													</button>
												</div>
											))}
										</div>
									) : (
										<div className="text-gray-500 text-center py-4">
											Нет подключенных Max аккаунтов
										</div>
									)}

									{/* Connect new Max account via QR */}
									<div className="border-t pt-6">
										<button
											onClick={() => setMaxAuthDialogOpen(true)}
											className="px-4 py-2 text-white rounded-lg transition-colors"
											style={{ backgroundColor: colors.primary }}
											onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primaryHover)}
											onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.primary)}
										>
											Подключить Max аккаунт
										</button>
										<p className="text-xs text-gray-500 mt-2">
											Отсканируйте QR-код в приложении Max, чтобы подключить свой аккаунт.
											Это позволит писать кандидатам первыми и получать их ответы.
										</p>
									</div>
								</div>
							)}
						</div>
					</div>
				)}

				{/* Notifications Tab — TASKNEIROKLYUCH-647: Bot API + preferences рекрутера */}
				{activeTab === 'notifications' && (
					<div className="space-y-6">
						<TelegramNotificationCard />
						<NotificationPreferencesCard />
					</div>
				)}

				{/* Google Calendar Tab */}
				{activeTab === 'calendar' && (
					<div className="space-y-6">
						<h2 className="text-lg font-semibold text-gray-900">Календари</h2>

						{loadingCalendar ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-gray-500">
									<div className="flex items-center justify-center mb-2">
										<ArrowPathIcon className="w-8 h-8 animate-spin" style={{ color: colors.primary }} />
									</div>
									<p>Загрузка...</p>
								</div>
							</div>
						) : (
							<>
								{/* Status Card */}
								<div className="bg-white rounded-lg border border-gray-200 p-6">
									<div className="flex items-start justify-between">
										<div className="flex items-start gap-4">
											<div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.primary}15` }}>
												<CalendarIcon className="w-7 h-7" style={{ color: colors.primary }} />
											</div>
											<div>
												<h3 className="text-lg font-semibold text-gray-900">Google Calendar</h3>
												<p className="mt-1 text-sm text-gray-600">
													Автоматическое создание событий для назначенных собеседований
												</p>
												<div className="mt-3 flex items-center gap-2">
													{isGoogleCalendarConnected() ? (
														<>
															<CheckCircleIcon className="w-5 h-5 text-green-500" />
															<span className="text-sm text-green-600 font-medium">Подключено</span>
														</>
													) : (
														<>
															<XCircleIcon className="w-5 h-5 text-gray-400" />
															<span className="text-sm text-gray-500">Не подключено</span>
														</>
													)}
												</div>
											</div>
										</div>

										<div>
											{isGoogleCalendarConnected() ? (
												<button
													type="button"
													onClick={(e) => {
														e.preventDefault();
														e.stopPropagation();
														handleDisconnectGoogleCalendar();
													}}
													className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
												>
													Отключить
												</button>
											) : (
												<button
													onClick={handleConnectGoogleCalendar}
													disabled={connectingCalendar}
													className="px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
													style={{ backgroundColor: colors.primary }}
													onMouseEnter={(e) => !connectingCalendar && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
													onMouseLeave={(e) => !connectingCalendar && (e.currentTarget.style.backgroundColor = colors.primary)}
												>
													{connectingCalendar ? 'Подключение...' : 'Подключить'}
												</button>
											)}
										</div>
									</div>

									{isGoogleCalendarConnected() && (
										<div className="mt-6 pt-6 border-t border-gray-200">
											<h4 className="text-sm font-medium text-gray-900 mb-3">Возможности:</h4>
											<ul className="space-y-2 text-sm text-gray-600">
												<li className="flex items-center gap-2">
													<CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
													<span>Автоматическое создание событий при назначении собеседования</span>
												</li>
												<li className="flex items-center gap-2">
													<CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
													<span>Отправка приглашений кандидатам на email</span>
												</li>
												<li className="flex items-center gap-2">
													<CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
													<span>Автоматическое удаление события при отмене собеседования</span>
												</li>
												<li className="flex items-center gap-2">
													<CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
													<span>Напоминания: email за 24 часа, popup за 30 минут</span>
												</li>
											</ul>
										</div>
									)}
								</div>

								{/* OAuth Settings */}
								<div className="bg-white rounded-lg border border-gray-200 p-6">
									<div className="flex items-center justify-between mb-4">
										<h3 className="text-md font-semibold text-gray-900">Настройки OAuth 2.0</h3>
										<button
											onClick={() => setShowSettings(!showSettings)}
											className="text-sm hover:underline"
											style={{ color: colors.primary }}
										>
											{showSettings ? 'Скрыть' : 'Показать'}
										</button>
									</div>

									{showSettings && (
										<div className="space-y-4">
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Client ID
												</label>
												<input
													type="text"
													placeholder="your-client-id.apps.googleusercontent.com"
													value={calendarSettings.clientId}
													onChange={(e) => setCalendarSettings({ ...calendarSettings, clientId: e.target.value })}
													className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
													style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
												/>
											</div>

											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Client Secret
												</label>
												<input
													type="password"
													placeholder="your-client-secret"
													value={calendarSettings.clientSecret}
													onChange={(e) => setCalendarSettings({ ...calendarSettings, clientSecret: e.target.value })}
													className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
													style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
												/>
											</div>

											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Redirect URI
												</label>
												<input
													type="text"
													value={calendarSettings.redirectUri}
													onChange={(e) => setCalendarSettings({ ...calendarSettings, redirectUri: e.target.value })}
													className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
													style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
												/>
												<p className="mt-1 text-xs text-gray-500">
													Используйте этот URI при создании OAuth credentials в Google Cloud Console
												</p>
											</div>

											<div className="flex items-center gap-2">
												<button
													onClick={handleSaveCalendarSettings}
													disabled={savingSettings || clearingSettings || !calendarSettings.clientId || !calendarSettings.clientSecret}
													className="px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
													style={{ backgroundColor: colors.primary }}
													onMouseEnter={(e) => !savingSettings && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
													onMouseLeave={(e) => !savingSettings && (e.currentTarget.style.backgroundColor = colors.primary)}
												>
													{savingSettings ? 'Сохранение...' : 'Сохранить настройки'}
												</button>
												<button
													onClick={handleClearCalendarSettings}
													disabled={clearingSettings || savingSettings}
													className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
												>
													{clearingSettings ? 'Очистка...' : 'Очистить'}
												</button>
											</div>
										</div>
									)}
								</div>

								{/* Instructions */}
								{!isGoogleCalendarConnected() && (
									<div className="rounded-lg p-4 border" style={{ backgroundColor: `${colors.primary}10`, borderColor: `${colors.primary}30` }}>
										<h4 className="text-sm font-medium mb-2" style={{ color: colors.primary }}>Как подключить:</h4>
										<ol className="list-decimal list-inside space-y-1 text-sm" style={{ color: colors.primary }}>
											<li>Создайте проект в <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
											<li>Включите Google Calendar API</li>
											<li>Создайте OAuth 2.0 Client ID (тип: Web application)</li>
											<li>Добавьте Redirect URI указанный выше в настройках OAuth</li>
											<li>Скопируйте Client ID и Client Secret в поля выше и сохраните</li>
											<li>Нажмите кнопку "Подключить" и авторизуйтесь в Google</li>
										</ol>
									</div>
								)}
							</>
						)}
													{/* TASKNEIROKLYUCH-429: Яндекс.Календарь (через CalDAV + Basic Auth) */}
							<div className="border-t border-gray-200 pt-6 mt-6">
								<div className="flex items-center justify-between mb-4">
									<div className="flex items-center gap-3">
										<div className="rounded-lg p-2.5 bg-red-50">
											<CalendarIcon className="h-6 w-6 text-red-600" />
										</div>
										<div>
											<h3 className="text-base font-semibold text-gray-900">Яндекс.Календарь</h3>
											<p className="text-sm text-gray-500">
												{isYandexCalendarConnected() ? 'Подключено' : 'Не подключено'}
											</p>
										</div>
									</div>
									{isYandexCalendarConnected() && (
										<button
											onClick={handleDisconnectYandexCalendar}
											className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
										>
											Отключить
										</button>
									)}
								</div>
								<p className="text-sm text-gray-600 mb-3">
									Синхронизация занятых слотов и автоматическое создание встреч в Яндекс.Календаре через CalDAV.
								</p>
								{!isYandexCalendarConnected() && (
									<div className="space-y-3">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Email Яндекса
											</label>
											<input
												type="email"
												value={yandexLogin}
												onChange={(e) => setYandexLogin(e.target.value)}
												placeholder="user@yandex.ru"
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
												style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Пароль приложения
											</label>
											<input
												type="password"
												value={yandexAppPassword}
												onChange={(e) => setYandexAppPassword(e.target.value)}
												placeholder="xxxxxxxxxxxxxxxx"
												autoComplete="off"
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 font-mono"
												style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
											/>
											<p className="mt-1 text-xs text-gray-500">
												Создаётся в <a href="https://passport.yandex.ru/profile" target="_blank" rel="noopener noreferrer" className="underline">passport.yandex.ru → Пароли приложений → CalDAV-сервер</a>.
												Это не основной пароль от аккаунта.
											</p>
										</div>
										<button
											onClick={handleConnectYandexCalendar}
											disabled={connectingYandexCalendar || !yandexLogin.trim() || !yandexAppPassword.trim()}
											className="px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
											style={{ backgroundColor: colors.primary }}
											onMouseEnter={(e) => !connectingYandexCalendar && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
											onMouseLeave={(e) => !connectingYandexCalendar && (e.currentTarget.style.backgroundColor = colors.primary)}
										>
											{connectingYandexCalendar ? 'Подключение...' : 'Подключить'}
										</button>
									</div>
								)}
							</div>					</div>
				)}
			</div>

			{/* Authentication Modal */}
			{authModal.isOpen && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 max-w-md w-full">
						<h3 className="text-lg font-semibold mb-4">
							{authModal.step === 'code' ? 'Введите код из Telegram' : 'Двухфакторная аутентификация'}
						</h3>

						{authModal.step === 'code' ? (
							<div>
								<p className="text-sm text-gray-600 mb-4">
									Код отправлен на номер {authModal.phoneNumber}
								</p>
								<input
									type="text"
									placeholder="Введите код (5-6 цифр)"
									value={authModal.code}
									onChange={(e) => setAuthModal({ ...authModal, code: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
									maxLength={6}
									style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
								/>
								<div className="flex justify-end mt-4 space-x-2">
									<button
										onClick={() => setAuthModal({ ...authModal, isOpen: false })}
										className="px-4 py-2 text-gray-600 hover:text-gray-800"
									>
										Отмена
									</button>
									<button
										onClick={handleVerifyTelegramCode}
										disabled={loading || !authModal.code || authModal.code.length < 5}
										className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
										style={{ backgroundColor: colors.primary }}
										onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
										onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.primary)}
									>
										{loading ? 'Проверка...' : 'Подтвердить'}
									</button>
								</div>
							</div>
						) : (
							<div>
								<p className="text-sm text-gray-600 mb-4">
									Введите пароль двухфакторной аутентификации
								</p>
								<input
									type="password"
									placeholder="Пароль"
									value={authModal.password}
									onChange={(e) => setAuthModal({ ...authModal, password: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
									style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
								/>
								<div className="flex justify-end mt-4 space-x-2">
									<button
										onClick={() => setAuthModal({ ...authModal, isOpen: false })}
										className="px-4 py-2 text-gray-600 hover:text-gray-800"
									>
										Отмена
									</button>
									<button
										onClick={handleVerify2FA}
										disabled={loading || !authModal.password}
										className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
										style={{ backgroundColor: colors.primary }}
										onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.primaryHover)}
										onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = colors.primary)}
									>
										{loading ? 'Проверка...' : 'Подтвердить'}
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Max QR authentication dialog */}
			<MaxAuthDialog
				isOpen={maxAuthDialogOpen}
				onClose={() => setMaxAuthDialogOpen(false)}
				onSuccess={() => {
					setMaxAuthDialogOpen(false);
					fetchMessengerAccounts();
				}}
			/>
		</div>
	);
};
